import { Inbox, type LucideIcon } from 'lucide-react';
import type { ReactElement, ReactNode } from 'react';

/**
 * "Nothing here" and "we could not look" are different answers and must look
 * different — see `ErrorState`. This one means the query succeeded and the
 * result was empty.
 */
export function EmptyState({
  title,
  body,
  action,
  icon: Icon = Inbox,
}: {
  title: string;
  body?: string;
  action?: ReactNode;
  /** Override to say what KIND of nothing this is — no results vs. never ran. */
  icon?: LucideIcon;
}): ReactElement {
  return (
    <div className="flex flex-col items-center gap-[var(--space-2)] rounded-[var(--radius-md)] border border-dashed border-line p-8 text-center">
      {/*
        Decorative: the title states the same thing in words, so announcing the
        motif would repeat it. It is muted rather than brand-coloured — an empty
        state is not an accent opportunity (§16.3.1), and a tenant whose primary
        is red would make "nothing here yet" read as a failure.

        The motif is a stack of three cards with the front one empty: it draws
        the shape of the thing that is missing, which a lone circled glyph does
        not. The two behind it are inline SVG stroked in `currentColor`, so they
        introduce no colour of their own and follow the wrapper's token — the
        rearmost dashed, because a dashed outline is the same "this is an
        absence" vocabulary as the container's own border.

        `line-strong`, not `line`: at 1px on a bright card the lighter hairline
        is not visible at all, which is the same measurement `.chart-guides`
        records for its quarter marks.

        The front card is HTML rather than a third `<rect>` so its glyph can be
        centred by flexbox. Centring it with `-translate-x-1/2` would put the
        motif at the mercy of the global `prefers-reduced-motion` rule, which
        sets `transform: none !important` — a reduced-motion reader would get a
        glyph sitting in the corner of its own card.

        No SVG `id` anywhere in here, deliberately: the source scan matches
        `#[0-9a-f]{6}` on stripped source, so a `url(#facade)` reference would
        fail the colour-literal test for a reason nobody would ever guess.
      */}
      <span aria-hidden="true" className="relative mb-1 block h-9 w-12 text-line-strong">
        <svg
          viewBox="0 0 48 36"
          fill="none"
          stroke="currentColor"
          strokeWidth={1}
          className="absolute inset-0 h-full w-full"
        >
          {/* `rx` is an SVG *geometry* property, so the class sets it from the
              tenant's radius scale and the attribute stays as the fallback for
              anything that does not honour it. A tenant on `radiusScale: none`
              gets a square motif, like the rest of its console. */}
          <rect
            x="8.5"
            y="0.5"
            width="39"
            height="27"
            rx="3"
            strokeDasharray="3 3"
            className="[rx:var(--radius-sm)]"
          />
          <rect x="4.5" y="4.5" width="39" height="27" rx="3" className="[rx:var(--radius-sm)]" />
        </svg>
        <span className="absolute bottom-0 left-0 flex h-7 w-10 items-center justify-center rounded-[var(--radius-sm)] border border-line-strong bg-surface text-muted">
          <Icon size={18} strokeWidth={1.75} />
        </span>
      </span>
      <p className="text-[length:var(--text-sm)] font-medium text-body">{title}</p>
      {body ? (
        <p className="max-w-prose text-[length:var(--text-sm)] text-muted">{body}</p>
      ) : null}
      {action}
    </div>
  );
}
